import { useRef } from "preact/hooks";
import type { ThreadSummary } from "../api";
import { shortDate, who } from "../format";
import { Icon } from "./icons";

const LONG_PRESS_MS = 500;

export function ThreadList({ threads, selected, onOpen, loading, hasMore, onMore, emptyText, sent = false, picked, onPick }: {
  threads: ThreadSummary[];
  sent?: boolean;
  selected?: string;
  onOpen: (t: ThreadSummary) => void;
  loading: boolean;
  hasMore: boolean;
  onMore: () => void;
  emptyText: string;
  // Sélection multiple : conversations cochées, et bascule de l'une d'elles.
  picked: Set<string>;
  onPick: (id: string) => void;
}) {
  // Appui long sur téléphone : commence la sélection, comme dans les messageries mobiles.
  const press = useRef<{ timer?: ReturnType<typeof setTimeout>; fired: boolean }>({ fired: false });
  const startPress = (id: string) => {
    press.current.fired = false;
    clearTimeout(press.current.timer);
    press.current.timer = setTimeout(() => {
      press.current.fired = true;
      navigator.vibrate?.(20);
      onPick(id);
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => clearTimeout(press.current.timer);
  const selecting = picked.size > 0;

  if (!loading && threads.length === 0) return <div class="empty">{emptyText}</div>;
  return (
    <div class="threads" role="list">
      {threads.map((t) => {
        const m = t.latest;
        const isPicked = picked.has(t.id);
        const activate = () => {
          if (press.current.fired) { press.current.fired = false; return; }
          if (selecting) onPick(t.id);
          else onOpen(t);
        };
        return (
          <div
            key={t.id} role="listitem" tabIndex={0}
            class={`thread${t.unread ? " unread" : ""}${selected === t.id ? " active" : ""}${isPicked ? " picked" : ""}`}
            onClick={activate}
            onKeyDown={(e) => { if (e.key === "Enter") activate(); if (e.key === " ") { e.preventDefault(); onPick(t.id); } }}
            onTouchStart={() => startPress(t.id)} onTouchEnd={cancelPress} onTouchMove={cancelPress}
            onContextMenu={(e) => { if (press.current.fired) e.preventDefault(); }}
          >
            <button class="pick" role="checkbox" aria-checked={isPicked} aria-label={`Sélectionner : ${m.subject || "(sans objet)"}`}
              onClick={(e) => { e.stopPropagation(); onPick(t.id); }}>
              {isPicked ? <Icon name="check" size={16} /> : <span class="initial">{(sent ? m.to[0] ? who(m.to[0]) : "?" : who(m.from)).trim().charAt(0).toUpperCase()}</span>}
            </button>
            <span class="who">
              {sent ? `À : ${m.to.map(who).join(", ")}` : who(m.from)} {t.count > 1 && <span class="count">{t.count}</span>}
            </span>
            <span class="meta when">
              {t.hasAttachments && <Icon name="clip" size={14} />}
              {t.starred && <span class="star"><Icon name="star" filled size={14} /></span>}
              {shortDate(t.lastAt)}
            </span>
            <span class="subject">{m.subject || "(sans objet)"}</span>
            <span class="snippet">{m.snippet ?? m.address}</span>
          </div>
        );
      })}
      {loading && <div class="empty">Chargement…</div>}
      {hasMore && !loading && <button class="btn more" onClick={onMore}>Afficher plus</button>}
    </div>
  );
}
